import React, { useEffect, lazy } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Router, Switch, Redirect } from "react-router-dom";

import {
	AlertManager,
	MatchReportPage,
	ModalManager,
	PermissionContext,
	SpinnerOverlay,
} from "components";
import { PrivateRoute, AnonymousRoute, LogoutRoute } from "components/route";
import { PublicRoute } from "components/route/publicRoute";
import RedirectToAvailableSideMenuItem from "components/route/redirectToAvailableSideMenuItem";
import AuthPage from "containers/authPage/authPage";
import { getBoardingQueuePaths } from "containers/boarding/details/boardingApplicationTabs";
import CancelEmailChange from "containers/cancelEmailChange";
import { getChargebacksDetailsTabsPaths } from "containers/chargebacks/details/chargebacksDetailsTabs";
import { getChargebacksTabsPaths } from "containers/chargebacks/queue/tabs/tabs";
import { getCommissionsTabsPaths } from "containers/commissions/tabs/tabs";
import ConfirmEmailChange from "containers/confirmEmailChange";
import { getCreditReviewDetailsTabsPaths } from "containers/creditReview/details/creditReviewDetailsTabs";
import { PageNotFound } from "containers/errors/pageNotFound";
import { ForgotPasswordPage } from "containers/forgotPasswordPage";
import { ForgotPasswordSuccessPage } from "containers/forgotPasswordSuccessPage";
import { LoginPage } from "containers/loginPage";
import { getMerchantTabsPaths } from "containers/merchantList/merchantInfo/merchantTabs";
import { ResetPasswordLinkErrorPage } from "containers/resetPasswordLinkErrorPage";
import { ResetPasswordPage } from "containers/resetPasswordPage";
import { ResetPasswordSuccessPage } from "containers/resetPasswordSuccessPage";
import { getAgentTabsPaths } from "containers/systemSetup/tabs/agentSetup/editAgent/editAgentTabs";
import { getUnderwritingPaths } from "containers/underwriting/application/underwritingTabs";
import MerchantGofacReport from "containers/underwriting/reports/merchantGofacReport";
import MerchantGverifyReport from "containers/underwriting/reports/merchantGverifyReport";
import MerchantTINCheckReport from "containers/underwriting/reports/merchantTINCheckReport";
import OwnerGIdentifyReport from "containers/underwriting/reports/ownerGIdentifyReport";
import OwnerGofacReport from "containers/underwriting/reports/ownerGofacReport";
import {
	ChargebacksPages,
	CommissionsPages,
	CreditReviewPages,
	FeatureManager,
	FeesPages,
	URLS,
	UserProfilePage,
} from "globalConstants";
import {
	applicationListPermissionSet,
	boardingQueuePermissionSet,
	creditReviewPermissionSet,
	editTerminalPageSet,
	equipmentOrderingPermissionSet,
	hasPermissions,
	history,
	installationPermissionSet,
	merchantPermissionSet,
	permissionList,
	qaQueuePermissionSet,
	retry,
	terminalSolutionPermissionSet,
	underwritingDetailPagePermissionSet,
	underwritingPagePermissionSet,
	underwritingQueuePermissionSet,
	userPermissionSet,
	uwDecisionPermissionSet,
	viewAppPermissionSet,
	viewBoardingPagePermissionSet,
	viewResidualsTabSet,
	viewScheduleAOrResidualSplitsPermissionSet,
} from "helpers";
import { gb } from "helpers/growthBook";
import { useSignalR, useClearCache, useCheckTokenChange } from "hooks";
import { userActions } from "store";
import { getCurrentUserId } from "store/user/user.reducer";

import { getApplicationPaths } from "../application/applicationEdit/components/applicationTabs";
import { ApplicationList } from "../application/list";
import Search from "../search";

// Main Pages
const AddMPA = lazy(() =>
	retry(() =>
		import(/* webpackChunkName: "AddMPA" */ "../mpaUpload/addMPA/addMPA"),
	),
);
const UnderwritingQueue = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "UnderwritingQueue" */ "../underwriting/queue"
		),
	),
);
const CreditReviewQueue = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "CreditReviewQueue" */ "../creditReview/index.tsx"
		),
	),
);
const BoardingListing = lazy(() =>
	retry(() =>
		import(/* webpackChunkName: "BoardingListing" */ "../boarding/queue"),
	),
);

const InstallationQueue = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "InstallationQueue" */ "containers/installation/queue"
		),
	),
);
const MerchantList = lazy(() =>
	retry(() =>
		import(/* webpackChunkName: "MerchantList" */ "../merchantList"),
	),
);
const RiskMonitoring = lazy(() =>
	retry(() =>
		import(/* webpackChunkName: "RiskMonitoring" */ "../risk/queue"),
	),
);

const TerminalSolution = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "TerminalSolutionQueue" */ "../terminalSolution/queue"
		),
	),
);
const MerchantRejectHistory = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "MerchantRejectHistory" */ "../risk/queue/tabs/achRejectsTab/merchantRejectHistory"
		),
	),
);

const Residuals = lazy(() =>
	retry(() => import(/* webpackChunkName: "Residuals" */ "../residuals")),
);
const SystemSetup = lazy(() =>
	retry(() => import(/* webpackChunkName: "SystemSetup" */ "../systemSetup")),
);

const ReportsDashboard = lazy(() =>
	retry(() =>
		import(/* webpackChunkName: "ReportsDashboard" */ "../reports"),
	),
);

const Commissions = lazy(() =>
	retry(() => import(/* webpackChunkName: "Commissions" */ "../commissions")),
);

const Wizard = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "Wizard Commissions" */ "../commissions/wizard"
		),
	),
);

// MPA
const EditMPA = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditMPA" */ "../mpaUpload/editMPA/editMPA"
		),
	),
);

// Chargebacks
const ChargebacksQueue = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "CreditReviewQueue" */ "../chargebacks/index.tsx"
		),
	),
);

const ChargebacksDetails = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "CreditReviewQueue" */ "../chargebacks/details"
		),
	),
);

// Application Component Pages
const ApplicationEntry = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "ApplicationEntry" */ "../application/applicationEntry"
		),
	),
);
const ApplicationEdit = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "ApplicationEdit" */ "../application/applicationEdit"
		),
	),
);
const ApplicationView = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "ApplicationView" */ "../application/applicationView"
		),
	),
);
const ApplicationPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "ApplicationPend" */ "../application/applicationPend/applicationPend"
		),
	),
);

// Underwriting Component Pages
const UnderwritingPage = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "UnderwritingPage" */ "../underwriting/application/underwritingPage"
		),
	),
);
const UnderwritingAddPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "UnderwritingAddPend" */ "../underwriting/underwritingPend/underwritingAddPend"
		),
	),
);
const UnderwritingPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "UnderwritingPend" */ "../underwriting/underwritingPend/underwritingPend"
		),
	),
);
const AddZendeskTicketUW = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddZendeskTicketUW" */ "../underwriting/addZendeskTicket"
		),
	),
);

const ExperianReportPage = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "ExperianReportPage" */ "../underwriting/experianReport"
		),
	),
);
// Credit Review Details Page
const CreditReviewDetails = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "CreditReviewDetails" */ "../creditReview/details"
		),
	),
);

// Boarding Component Pages
const BoardingApplicationInfo = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "BoardingApplicationInfo" */ "../boarding/details"
		),
	),
);
const BoardingAddPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "BoardingAddPend" */ "../boarding/boardingPend/boardingAddPend"
		),
	),
);
const BoardingPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "BoardingPend" */ "../boarding/boardingPend/boardingPend"
		),
	),
);

// TID Request & Equipment Queue Component Pages
const TidRequestEquipmentQueue = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "TidRequestEquipmentQueue" */ "../tidRequestEquipmentQueue"
		),
	),
);
const TidEquipmentInfo = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "TidEquipmentInfo" */ "../tidRequestEquipmentDetails"
		),
	),
);
const TidEquipmentAddPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "TidEquipmentAddPend" */ "../tidRequestEquipmentDetails/pendDetails/addPend"
		),
	),
);
const TidEquipmentUpdatePend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "TidEquipmentUpdatePend" */ "../tidRequestEquipmentDetails/pendDetails/updatePend"
		),
	),
);

const AddTerminalSolutionTidEquipment = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddTerminalSolutionTidEquipment" */ "../tidRequestEquipmentDetails/terminalSolution/addTerminalSolution"
		),
	),
);
const EditTerminalSolutionTidEquipment = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditTerminalSolutionTidEquipment" */ "../tidRequestEquipmentDetails/terminalSolution/editTerminalSolution"
		),
	),
);

// Merchant Info Component Pages
const MerchantInfo = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "MerchantInfo" */ "../merchantList/merchantInfo"
		),
	),
);
const AddMerchant = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddMerchant" */ "../merchantList/addMerchant"
		),
	),
);
const EditMerchant = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditMerchant" */ "../merchantList/editMerchant"
		),
	),
);
const MerchantAddPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "MerchantAddPend" */ "../merchantList/merchantPend/merchantAddPend"
		),
	),
);
const MerchantPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "MerchantPend" */ "../merchantList/merchantPend/merchantPend"
		),
	),
);
const MerchantHierarchy = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "MerchantHierarchy" */ "../merchantList/merchantInfo/tabsMerchantInfo/hierarchy/hierarchy"
		),
	),
);

const AddTerminalSolutionMerchant = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddTerminalSolution" */ "../merchantList/merchantInfo/tabsMerchantInfo/terminalSolution/addTerminalSolution"
		),
	),
);

const EditTerminalSolutionMerchant = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditTerminalSolution" */ "../merchantList/merchantInfo/tabsMerchantInfo/terminalSolution/editTerminalSolution"
		),
	),
);

const TransactionsInBatch = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "TransactionsInBatch" */
			"../merchantList/merchantInfo/tabsMerchantInfo/batchHistory/transactionsInBatch"
		),
	),
);

// Installation
const InstallationDetails = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "InstallationDetails" */ "containers/installation/details"
		),
	),
);
const InstallationDetailsAddPend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "InstallationDetailsAddPend" */ "containers/installation/details/pendDetails/addPend"
		),
	),
);
const InstallationDetailsUpdatePend = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "InstallationDetailsUpdatePend" */ "containers/installation/details/pendDetails/updatePend"
		),
	),
);

const EditTerminalSolutionInstallation = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditTerminalSolutionInstallation" */ "containers/installation/details/terminalSolution/editTerminalSolution"
		),
	),
);

// BackOffice
const EditUser = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditUser" */ "../systemSetup/tabs/users/editUser"
		),
	),
);
const AddUser = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddUser" */ "../../containers/systemSetup/tabs/users/addUser"
		),
	),
);
const AddAgent = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddAgent" */ "../../containers/systemSetup/tabs/agentOfficeSetup/addAgent/addAgent"
		),
	),
);
const EditAgentOffice = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditAgentOffice" */ "../systemSetup/tabs/agentOfficeSetup/editViewAgentOffice/tabs/generalInfo/editAgentOffice"
		),
	),
);
const EditAgentTabs = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditAgentTabs" */ "../../containers/systemSetup/tabs/agentSetup/editAgent"
		),
	),
);
const AgentScheduleAView = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AgentScheduleAView" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/scheduleA/agentScheduleAView"
		),
	),
);
const AddAgentScheduleA = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddAgentScheduleA" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/scheduleA/addAgentScheduleA"
		),
	),
);
const EditAgentScheduleA = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditAgentScheduleA" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/scheduleA/editAgentScheduleA"
		),
	),
);
const AddFeeTemplate = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddFeeTemplate" */ "../systemSetup/tabs/agentOfficeSetup/feeTemplate/addFeeTemplate"
		),
	),
);
const EditFeeTemplate = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditFeeTemplate" */ "../systemSetup/tabs/agentOfficeSetup/feeTemplate/editFeeTemplate"
		),
	),
);
const AddAdditionalFeeTemplate = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddAdditionalFeeTemplate" */ "../systemSetup/tabs/agentOfficeSetup/additionalFeeTemplate/addAdditionalFeeTemplate"
		),
	),
);
const EditAdditionalFeeTemplate = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditAdditionalFeeTemplate" */ "../systemSetup/tabs/agentOfficeSetup/additionalFeeTemplate/editAdditionalFeeTemplate"
		),
	),
);
const AddMonthlyFeeTemplate = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddMonthlyFeeTemplate" */ "../systemSetup/tabs/agentOfficeSetup/monthlyFeeTemplate/addMonthlyFeeTemplate"
		),
	),
);
const EditMonthlyFeeTemplate = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditMonthlyFeeTemplate" */ "../systemSetup/tabs/agentOfficeSetup/monthlyFeeTemplate/editMonthlyFeeTemplate"
		),
	),
);
const AchFeeTemplateAdd = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AchFeeTemplateAdd" */ "../systemSetup/tabs/agentOfficeSetup/editViewAgentOffice/tabs/achFee/achFeeTemplateAdd"
		),
	),
);
const AchFeeTemplateEdit = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AchFeeTemplateEdit" */ "../systemSetup/tabs/agentOfficeSetup/editViewAgentOffice/tabs/achFee/achFeeTemplateEdit"
		),
	),
);
const AriseFeeTemplateAdd = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AriseFeeTemplateAdd" */ "../systemSetup/tabs/agentOfficeSetup/editViewAgentOffice/tabs/ariseFee/ariseFeeTemplateAdd"
		),
	),
);
const AriseFeeTemplateEdit = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AriseFeeTemplateEdit" */ "../systemSetup/tabs/agentOfficeSetup/editViewAgentOffice/tabs/ariseFee/ariseFeeTemplateEdit"
		),
	),
);
const AddSplitProfile = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddSplitProfile" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/residualsSplitProfile/addSplitProfile"
		),
	),
);
const EditSplitProfile = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditSplitProfile" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/residualsSplitProfile/editSplitProfile"
		),
	),
);
const ViewSplitProfile = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "ViewSplitProfile" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/residualsSplitProfile/viewSplitProfile"
		),
	),
);
const AddCommissionSplit = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddCommissionsSplit" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/commissionSplits/addCommissionSplit"
		),
	),
);
const EditCommissionSplit = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditCommissionsSplit" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/commissionSplits/editCommissionSplit"
		),
	),
);
const CommissionSplitsView = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "ViewCommissionsSplit" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/commissionSplits/view"
		),
	),
);

const AppEditContactInfo = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AppEditContactInfo" */ "../../components/pages/appEditContactInfo"
		),
	),
);

const EditAgentSetupPaymentMethod = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditAgentSetupPaymentMethod" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/billingInfo/editPaymentMethod"
		),
	),
);
const AddAgentSetupPaymentMethod = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddAgentSetupPaymentMethod" */ "../../containers/systemSetup/tabs/agentSetup/editAgent/tabs/billingInfo/addPaymentMethod"
		),
	),
);

const MerchantEditPaymentMethod = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "EditAgentSetupPaymentMethod" */ "../../containers/merchantList/merchantInfo/tabsMerchantInfo/billingInfo/editPaymentMethod"
		),
	),
);
const MerchantAddPaymentMethod = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "AddAgentSetupPaymentMethod" */ "../../containers/merchantList/merchantInfo/tabsMerchantInfo/billingInfo/addPaymentMethod"
		),
	),
);

//User Profile

const UserProfileObsolete = lazy(() =>
	retry(() =>
		import(
			/* webpackChunkName: "UserProfilePage" */ "../userProfileObsolete"
		),
	),
);

const UserProfile = lazy(() =>
	retry(() =>
		import(/* webpackChunkName: "UserProfilePage" */ "../userProfile"),
	),
);

const App = () => {
	const dispatch = useDispatch();

	const currentUser = JSON.parse(localStorage.getItem("user"));

	const permissionLoading = useSelector(
		state => state.user.permissionLoading,
	);

	useEffect(() => {
		dispatch(userActions.getPermissions({ fromStore: true }));
	}, [dispatch]);

	useSignalR();
	useClearCache({ autoReload: true });

	useCheckTokenChange();
	const currentUserId = useSelector(getCurrentUserId);

	gb.setAttributes({
		id: currentUserId,
		agentOfficeId: currentUser?.company_id,
	});

	return (
		<>
			<AlertManager />
			<SpinnerOverlay />
			<PermissionContext.Provider
				value={{
					permissionLoading,
				}}
			>
				<Router history={history}>
					<ModalManager />
					<Switch>
						<PublicRoute
							path={URLS.cancelEmailChange}
							component={CancelEmailChange}
							pageName="Cancel Email Change"
						/>
						<PublicRoute
							path={URLS.confirmEmailChange}
							component={ConfirmEmailChange}
							pageName="Confirm Email Change"
						/>
						<AnonymousRoute
							path={URLS.login}
							component={
								FeatureManager.isEnabled("fusionAuth")
									? AuthPage
									: LoginPage
							}
							pageName="Authentication"
						/>
						<AnonymousRoute
							path="/forgot-password"
							component={ForgotPasswordPage}
							pageName="Forgot Password"
						/>
						<AnonymousRoute
							path="/forgot-password-success"
							component={ForgotPasswordSuccessPage}
							pageName="Forgot Password"
						/>
						<AnonymousRoute
							path="/reset-password"
							component={ResetPasswordPage}
							pageName="Reset Password"
						/>
						<AnonymousRoute
							path="/reset-password-success"
							component={ResetPasswordSuccessPage}
							pageName="Reset Password"
						/>
						<AnonymousRoute
							path="/reset-password-link-error"
							component={ResetPasswordLinkErrorPage}
							pageName="Reset Password"
						/>
						<AnonymousRoute
							path="/confirm-email-change"
							component={ResetPasswordLinkErrorPage}
							pageName="Confirm Email Change"
						/>

						<LogoutRoute exact path={URLS.logout} />
						<PrivateRoute
							exact
							path={["/", "/login"]}
							component={RedirectToAvailableSideMenuItem}
						/>
						<PrivateRoute
							exact
							path="/applications/upload-mpa/:tab?"
							component={AddMPA}
							requiredPermissions={
								permissionList.StartNewApplication
							}
							pageName="Upload MPA"
						/>
						<PrivateRoute
							exact
							path="/applications/upload-mpa/:id/:tab?"
							component={EditMPA}
							requiredPermissions={
								permissionList.StartNewApplication
							}
							pageName="Edit Upload MPA"
						/>
						<PrivateRoute
							exact
							path="/applications/entry"
							component={ApplicationEntry}
							requiredPermissions={
								permissionList.StartNewApplication
							}
							pageName="Application"
						/>
						<PrivateRoute
							exact
							path="/applications/:tab(all|signed-mpas|pre-applications|pended|drafts)?"
							component={ApplicationList}
							pageName="Application List"
							requiredPermissions={applicationListPermissionSet}
						/>
						<Redirect
							exact
							from="/applications/edit/:id"
							to="/applications/edit/:id/get-started"
						/>
						<PrivateRoute
							exact
							//Allows only certain tabs
							path={`/applications/edit/:id/:tab(${getApplicationPaths(
								false,
							).join("|")})`}
							component={ApplicationEdit}
							pageName="Application Edit"
							requiredPermissions={
								permissionList.StartNewApplication
							}
						/>
						<PrivateRoute
							exact
							//Allows only certain tabs
							path={`/applications/edit/:id/:tab/error`}
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<Redirect
							exact
							from="/applications/:id"
							to="/applications/:id/get-started"
						/>
						<PrivateRoute
							exact
							path={`/applications/:id/:tab(${getApplicationPaths(
								true,
							).join("|")})`}
							component={ApplicationView}
							pageName="Application View"
							requiredPermissions={viewAppPermissionSet}
						/>
						<PrivateRoute
							exact
							path={`/applications/:id/:tab/error`}
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/applications/:id/pend/:idPend"
							component={ApplicationPend}
							pageName="Pend"
							requiredPermissions={viewAppPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab"
							component={UnderwritingQueue}
							pageName="Underwriting List"
							requiredPermissions={underwritingQueuePermissionSet}
						/>
						<Redirect
							exact
							from="/underwriting/:tab/:id"
							to="/underwriting/:tab/:id/merchant-info"
						/>
						<PrivateRoute
							exact
							path={`/underwriting/:tab/:id/:underwritingTab(${getUnderwritingPaths().join(
								"|",
							)})`}
							component={UnderwritingPage}
							pageName="Underwriting"
							requiredPermissions={
								underwritingDetailPagePermissionSet
							}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/pend/add"
							component={UnderwritingAddPend}
							pageName="Add Pend"
							requiredPermissions={underwritingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/pend/:idPend"
							component={UnderwritingPend}
							pageName="Pend"
							requiredPermissions={underwritingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/:underwritingTab?/error"
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/:underwritingTab/merchantMatch"
							component={MatchReportPage}
							pageName="Match Report"
							requiredPermissions={underwritingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/tickets/new"
							component={AddZendeskTicketUW}
							pageName="Create Ticket"
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/:underwritingTab/merchant-gofac"
							component={MerchantGofacReport}
							pageName="Merchant gOfac"
							requiredPermissions={underwritingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/:underwritingTab/:ownerType/owner-gofac"
							component={OwnerGofacReport}
							pageName="Owner gOfac"
							requiredPermissions={underwritingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/:underwritingTab/:ownerType/owner-gidentify"
							component={OwnerGIdentifyReport}
							pageName="Owner gIdentify"
							requiredPermissions={underwritingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/:underwritingTab/gverify-result"
							component={MerchantGverifyReport}
							pageName="gVerify & gAuthenticate Result"
							requiredPermissions={underwritingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/:underwritingTab/tin-check"
							component={MerchantTINCheckReport}
							pageName="TIN check Result"
							requiredPermissions={underwritingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/underwriting/:tab/:id/:underwritingTab/:ownerType/experian"
							component={ExperianReportPage}
							pageName="Experian Report"
							requiredPermissions={underwritingPagePermissionSet}
						/>

						{!hasPermissions(boardingQueuePermissionSet) &&
							hasPermissions(qaQueuePermissionSet) && (
								<Redirect
									from="/boarding-queue/unassigned"
									to="/boarding-queue/qa-unassigned"
								/>
							)}
						{!hasPermissions(boardingQueuePermissionSet) &&
							hasPermissions(qaQueuePermissionSet) && (
								<Redirect
									from="/boarding-queue/in-progress"
									to="/boarding-queue/qa-unassigned"
								/>
							)}
						{!hasPermissions(boardingQueuePermissionSet) &&
							hasPermissions(qaQueuePermissionSet) && (
								<Redirect
									from="/boarding-queue/pended"
									to="/boarding-queue/qa-unassigned"
								/>
							)}
						{!hasPermissions(boardingQueuePermissionSet) &&
							hasPermissions(qaQueuePermissionSet) && (
								<Redirect
									from="/boarding-queue/completed"
									to="/boarding-queue/qa-unassigned"
								/>
							)}
						{!hasPermissions(qaQueuePermissionSet) &&
							hasPermissions(boardingQueuePermissionSet) && (
								<Redirect
									from="/boarding-queue/qa-unassigned"
									to="/boarding-queue/unassigned"
								/>
							)}
						{!hasPermissions(qaQueuePermissionSet) &&
							hasPermissions(boardingQueuePermissionSet) && (
								<Redirect
									from="/boarding-queue/qa-in-progress"
									to="/boarding-queue/unassigned"
								/>
							)}
						{!hasPermissions(qaQueuePermissionSet) &&
							hasPermissions(boardingQueuePermissionSet) && (
								<Redirect
									from="/boarding-queue/qa-completed"
									to="/boarding-queue/unassigned"
								/>
							)}
						<PrivateRoute
							exact
							path="/boarding-queue/:tab/"
							component={BoardingListing}
							pageName="Boarding Queue"
							requiredPermissions={viewBoardingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path={`/boarding-queue/:tab/:id/:boardingApplicationTab(${getBoardingQueuePaths().join(
								"|",
							)})`}
							component={BoardingApplicationInfo}
							pageName="Boarding"
							requiredPermissions={viewBoardingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path={`/boarding-queue/:tab/:id/:boardingApplicationTab/error`}
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>

						<PrivateRoute
							exact
							path={`/boarding-queue/:tab/:id/details/edit-contact-info`}
							component={AppEditContactInfo}
							pageName="Edit Contact Info"
							requiredPermissions={viewBoardingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/boarding-queue/:tab/:id/pend/add"
							component={BoardingAddPend}
							pageName="Add Pend"
							requiredPermissions={viewBoardingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/boarding-queue/:tab/:id/pend/:idPend"
							component={BoardingPend}
							pageName="Pend"
							requiredPermissions={viewBoardingPagePermissionSet}
						/>
						<PrivateRoute
							exact
							path="/applications/:id/view-document/error"
							component={PageNotFound}
							pageName="MPA not signed"
							errorTitle="MPA not signed"
						/>
						<PrivateRoute
							exact
							path="/tid-request-equipment-queue/:tab?"
							component={TidRequestEquipmentQueue}
							pageName="Tid Request"

							// requiredPermissions={equipmentOrderingPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/tid-request-equipment-queue/:tab/:id/terminal-solutions/add"
							component={AddTerminalSolutionTidEquipment}
							pageName="Add Terminal"
							requiredPermissions={
								permissionList.AddTerminalManually
							}
						/>
						<PrivateRoute
							exact
							path="/tid-request-equipment-queue/:tab/:id/terminal-solutions/:terminalId"
							component={EditTerminalSolutionTidEquipment}
							pageName="Edit Terminal"
							requiredPermissions={editTerminalPageSet}
						/>
						<PrivateRoute
							exact
							path="/tid-request-equipment-queue/:tab/:id/:infoTab"
							component={TidEquipmentInfo}
							pageName="TID Request & Equipment Info"

							// requiredPermissions={equipmentOrderingPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/tid-request-equipment-queue/:tab/:id/:infoTab/error"
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/tid-request-equipment-queue/:tab/:id/pend/add"
							component={TidEquipmentAddPend}
							pageName="Add Pend"
							requiredPermissions={equipmentOrderingPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/tid-request-equipment-queue/:tab/:id/pend/:idPend"
							component={TidEquipmentUpdatePend}
							pageName="Pend"
							requiredPermissions={equipmentOrderingPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/credit-review/:tab?"
							component={CreditReviewQueue}
							pageName={
								CreditReviewPages.CreditReview.displayName
							}
							requiredPermissions={creditReviewPermissionSet}
						/>
						<PrivateRoute
							exact
							path={`/credit-review/:tab/:id/:creditReviewDetailsTab(${getCreditReviewDetailsTabsPaths().join(
								"|",
							)})`}
							component={CreditReviewDetails}
							pageName="Credit Review Details"
							requiredPermissions={creditReviewPermissionSet}
						/>
						<PrivateRoute
							exact
							path={`/credit-review/:tab/:id/:creditReviewDetailsTab/error`}
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path={`/${
								ChargebacksPages.Chargebacks.name
							}/:chargebacksTab(${getChargebacksTabsPaths().join(
								"|",
							)})`}
							component={ChargebacksQueue}
							pageName={ChargebacksPages.Chargebacks.displayName}
							requiredPermissions={[
								permissionList.PeriodicCreditReview,
							]}
						/>
						<PrivateRoute
							exact
							path={`/${
								ChargebacksPages.Chargebacks.name
							}/:chargebacksTab(${getChargebacksTabsPaths().join(
								"|",
							)})/:id/:chargebacksDetailsTab(${getChargebacksDetailsTabsPaths().join(
								"|",
							)})`}
							component={ChargebacksDetails}
							pageName="Chargebacks Details"
							requiredPermissions={[
								permissionList.PeriodicCreditReview,
							]}
						/>
						<PrivateRoute
							exact
							path={`/${UserProfilePage.name}/obsolete`}
							component={UserProfileObsolete}
							pageName={UserProfilePage.displayName}
						/>

						<PrivateRoute
							exact
							path={`/${UserProfilePage.name}`}
							component={UserProfile}
							pageName={UserProfilePage.displayName}
						/>

						<PrivateRoute
							exact
							path="/installation-queue/:tab"
							component={InstallationQueue}
							pageName="Installation Queue"
							requiredPermissions={installationPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/installation-queue/:tab/:id/:infoTab"
							component={InstallationDetails}
							pageName="Installation Queue Details"
							requiredPermissions={installationPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/installation-queue/:tab/:id/:infoTab/error"
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/installation-queue/:tab/:id/pend/add"
							component={InstallationDetailsAddPend}
							pageName="Add Pend"
							requiredPermissions={installationPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/installation-queue/:tab/:id/pend/:idPend"
							component={InstallationDetailsUpdatePend}
							pageName="Pend"
							requiredPermissions={installationPermissionSet}
						/>
						{/* <PrivateRoute
							exact
							path="/installation-queue/:tab/:id/terminal/:terminalId"
							component={EditTerminalInstallation}
							pageName="Edit Terminal"
							requiredPermissions={editTerminalPageSet}
							
						/> */}
						<PrivateRoute
							exact
							path="/installation-queue/:tab/:id/terminal-solutions/:terminalId"
							component={EditTerminalSolutionInstallation}
							pageName="Edit Terminal"
							requiredPermissions={editTerminalPageSet}
						/>
						<Redirect
							exact
							from="/system-setup"
							to="/system-setup/users"
						/>
						<PrivateRoute
							exact
							path="/system-setup/:tab?"
							component={SystemSetup}
							pageName="System Setup"
						/>
						<PrivateRoute
							exact
							path="/system-setup/users/add"
							component={AddUser}
							pageName="Add User"
							requiredPermissions={userPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/system-setup/users/:id/:tab?"
							component={EditUser}
							pageName="Edit User"
							requiredPermissions={userPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/system-setup/users/:id/:tab?/error"
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/add-agent-office/:tab?"
							component={AddAgent}
							pageName="Add Agent Office"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/edit-agent-office/:companyId/add-fee-template"
							component={AddFeeTemplate}
							pageName="Add Fee Template"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/edit-agent-office/:companyId/edit-fee-template/:templateId"
							component={EditFeeTemplate}
							pageName="Edit Fee Template"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/edit-agent-office/:companyId/add-additional-fee-template"
							component={AddAdditionalFeeTemplate}
							pageName="Add Additional Fee Template"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/edit-agent-office/:companyId/edit-additional-fee-template/:templateId"
							component={EditAdditionalFeeTemplate}
							pageName="Edit Additional Fee Template"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/edit-agent-office/:companyId/add-monthly-fee-template"
							component={AddMonthlyFeeTemplate}
							pageName="Add Monthly Fee Template"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/edit-agent-office/:companyId/edit-monthly-fee-template/:templateId"
							component={EditMonthlyFeeTemplate}
							pageName="Edit Monthly Fee Template"
						/>
						<PrivateRoute
							exact
							path={`/system-setup/agent-office-setup/edit-agent-office/:companyId/${FeesPages.AchFeeAdd.name}`}
							component={AchFeeTemplateAdd}
							pageName={FeesPages.AchFeeAdd.displayName}
						/>
						<PrivateRoute
							exact
							path={`/system-setup/agent-office-setup/edit-agent-office/:companyId/${FeesPages.AchFeeEdit.name}/:templateId`}
							component={AchFeeTemplateEdit}
							pageName={FeesPages.AchFeeEdit.displayName}
						/>
						<PrivateRoute
							exact
							path={`/system-setup/agent-office-setup/edit-agent-office/:companyId/${FeesPages.AriseFeeAdd.name}`}
							component={AriseFeeTemplateAdd}
							pageName={FeesPages.AriseFeeAdd.displayName}
						/>
						<PrivateRoute
							exact
							path={`/system-setup/agent-office-setup/edit-agent-office/:companyId/${FeesPages.AriseFeeEdit.name}/:templateId`}
							component={AriseFeeTemplateEdit}
							pageName={FeesPages.AriseFeeAdd.displayName}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/edit-agent-office/:id/:tab?"
							component={EditAgentOffice}
							pageName="Edit Agent Office"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-office-setup/edit-agent-office/:id/:tab?/error"
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:agentId/residuals-split-profiles/add"
							component={AddSplitProfile}
							pageName="Add Residuals Split Profile"
							requiredPermissions={
								permissionList.CreateScheduleAOrResidualSplitProfileForAgentOfficeOrAgent
							}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:agentId/residuals-split-profiles/:id"
							component={EditSplitProfile}
							pageName="Edit Residuals Split Profile"
							requiredPermissions={
								permissionList.EditExistinScheduleAOrResidualSplitProfileForAgentOfficeOrAgent
							}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:agentId/residuals-split-profiles/view/:id"
							component={ViewSplitProfile}
							pageName="View Residuals Split Profile"
							requiredPermissions={
								viewScheduleAOrResidualSplitsPermissionSet
							}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:agentId/residuals-split-profiles/view/:id/error"
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:agentId/commission-split/add"
							component={AddCommissionSplit}
							pageName="Add Commission Splits"
							requiredPermissions={
								permissionList.CreateScheduleAOrResidualSplitProfileForAgentOfficeOrAgent
							}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:agentId/commission-split/:commissionId"
							component={EditCommissionSplit}
							pageName="Edit Commission Splits"
							requiredPermissions={
								permissionList.CreateScheduleAOrResidualSplitProfileForAgentOfficeOrAgent
							}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:id/commission-split/:commissionId/view"
							component={CommissionSplitsView}
							pageName="Commission Splits View"
						/>
						<Redirect
							exact
							from="/system-setup/agent-setup/edit-agent/:id"
							to="/system-setup/agent-setup/edit-agent/:id/general-info"
						/>
						<PrivateRoute
							exact
							path={`/system-setup/agent-setup/edit-agent/:id/:tab(${getAgentTabsPaths().join(
								"|",
							)})`}
							component={EditAgentTabs}
							pageName="Edit Agent"
						/>
						<PrivateRoute
							exact
							path={`/system-setup/agent-setup/edit-agent/:id/billing-information/:paymentMethodId/edit`}
							component={EditAgentSetupPaymentMethod}
							pageName="Edit Payment Method"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:id/billing-information/add"
							component={AddAgentSetupPaymentMethod}
							pageName="Add Payment Method"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:id/:tab?/error"
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:id/schedule-a/add"
							component={AddAgentScheduleA}
							pageName="Add Schedule A"
							requiredPermissions={
								permissionList.CreateScheduleAOrResidualSplitProfileForAgentOfficeOrAgent
							}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:id/schedule-a/:scheduleId"
							component={EditAgentScheduleA}
							pageName="Edit Schedule A"
							requiredPermissions={
								permissionList.EditExistinScheduleAOrResidualSplitProfileForAgentOfficeOrAgent
							}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:id/schedule-a/:scheduleId/view"
							component={AgentScheduleAView}
							pageName="View Schedule A"
							requiredPermissions={
								viewScheduleAOrResidualSplitsPermissionSet
							}
						/>
						<PrivateRoute
							exact
							path="/system-setup/agent-setup/edit-agent/:id/schedule-a/:scheduleId/error"
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/merchant/list"
							component={MerchantList}
							pageName="Merchant List"
							requiredPermissions={merchantPermissionSet}
						/>
						{!hasPermissions(permissionList.EquipmentBilling) && (
							<Redirect
								exact
								from="/terminal-solution-queue/equipment-billing"
								to="/terminal-solution-queue/all-solutions"
							/>
						)}
						<PrivateRoute
							exact
							path="/terminal-solution-queue/:tab/"
							component={TerminalSolution}
							pageName="Terminal Solution"
							requiredPermissions={terminalSolutionPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/hierarchy"
							component={MerchantHierarchy}
							pageName="Merchant Hierarchy"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path={`/merchant/list/info/:id/:tab(${getMerchantTabsPaths().join(
								"|",
							)})`}
							component={MerchantInfo}
							pageName="Merchant Info"
							requiredPermissions={[
								...merchantPermissionSet,
								...uwDecisionPermissionSet,
							]}
						/>
						<PrivateRoute
							exact
							path={`/merchant/list/info/:id/:tab/error`}
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/pend/add"
							component={MerchantAddPend}
							pageName="Add Pend"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/pend/:idPend"
							component={MerchantPend}
							pageName="Pend"
							requiredPermissions={merchantPermissionSet}
						/>
						<Redirect
							exact
							from="/merchant/list/info/:id"
							to="/merchant/list/info/:id/general"
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/results/:applicationId/merchantMatch"
							component={MatchReportPage}
							pageName="Match Report"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/results/:applicationId/merchant-gofac"
							component={MerchantGofacReport}
							pageName="Merchant gOfac"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/results/:applicationId/:ownerType/owner-gofac"
							component={OwnerGofacReport}
							pageName="Owner gOfac"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/results/:applicationId/:ownerType/owner-gidentify"
							component={OwnerGIdentifyReport}
							pageName="Owner gIdentify"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/results/:applicationId/gverify-result"
							component={MerchantGverifyReport}
							pageName="gVerify & gAuthenticate Result"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/results/:applicationId/tin-check"
							component={MerchantTINCheckReport}
							pageName="TIN check Result"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/results/:applicationId/:ownerType/experian"
							component={ExperianReportPage}
							pageName="Experian Report"
							requiredPermissions={merchantPermissionSet}
						/>
						<PrivateRoute
							exact
							path={`/merchant/list/info/:id/billing-information/:paymentMethodId/edit`}
							component={MerchantEditPaymentMethod}
							pageName="Edit Payment Method"
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/billing-information/add"
							component={MerchantAddPaymentMethod}
							pageName="Add Payment Method"
						/>
						<PrivateRoute
							exact
							path="/merchant/list/addMerchant"
							component={AddMerchant}
							pageName="Add Merchant"
							requiredPermissions={[
								permissionList.ManualMerchantEntry,
							]}
						/>
						<PrivateRoute
							exact
							path="/risk/:tab/"
							component={RiskMonitoring}
							pageName="Risk Monitoring"
							requiredPermissions={[permissionList.RiskTickets]}
						/>
						<PrivateRoute
							exact
							path="/risk/ach-rejects/:id"
							component={MerchantRejectHistory}
							pageName="Merchant Reject History"
							requiredPermissions={[permissionList.RiskTickets]}
						/>
						<Redirect exact from="/risk" to="/risk/ach-rejects" />

						<PrivateRoute
							exact
							path="/merchant/list/info/edit/:id/general"
							component={EditMerchant}
							pageName="Edit General"
							requiredPermissions={
								permissionList.ManualMerchantEntry
							}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/edit/:id/general/error"
							// Used to catch page not found error
							component={PageNotFound}
							pageName="Page not found"
							errorTitle="Page not found"
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:merchantId/terminal-solutions/:terminalId/edit"
							component={EditTerminalSolutionMerchant}
							pageName="Edit Terminal"
							requiredPermissions={editTerminalPageSet}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:merchantId/terminal-solutions/add"
							component={AddTerminalSolutionMerchant}
							pageName="Add Terminal"
							requiredPermissions={
								permissionList.AddTerminalManually
							}
						/>
						<PrivateRoute
							exact
							path="/merchant/list/info/:id/batch-history/:batchNumber/transactions-batch"
							component={TransactionsInBatch}
							pageName="Transactions in Batch"
							requiredPermissions={
								permissionList.ViewBatchHistory
							}
						/>
						<PrivateRoute
							exact
							path="/residuals/:tab?"
							component={Residuals}
							pageName="Residuals"
							requiredPermissions={viewResidualsTabSet}
						/>
						<PrivateRoute
							exact
							path="/reports"
							component={ReportsDashboard}
							pageName="Reports Dashboard"
							requiredPermissions={false}
						/>
						<PrivateRoute
							exact
							path={`/${
								CommissionsPages.Commissions.name
							}/:commissionsTab(${getCommissionsTabsPaths().join(
								"|",
							)})`}
							component={Commissions}
							pageName={CommissionsPages.Commissions.displayName}
							requiredPermissions={false}
						/>
						<PrivateRoute
							exact
							path={`/${
								CommissionsPages.Commissions.name
							}/:commissionsTab(${getCommissionsTabsPaths().join(
								"|",
							)})/${CommissionsPages.AddRevenueSource.name}`}
							component={Wizard}
							pageName={
								CommissionsPages.AddRevenueSource.displayName
							}
							requiredPermissions={false}
						/>
						<PrivateRoute
							path="/search"
							component={Search}
							pageName="Search"
						/>
						{/* ATTENTION!!! This route have to be the last one always. The route must be rendered only if path does not match any previous routes */}
						<PrivateRoute
							component={PageNotFound}
							pageName="Not found"
						/>
					</Switch>
				</Router>
			</PermissionContext.Provider>
		</>
	);
};

export default App;
